// Per-locale i18next instances for the localized `[lang]` routes.
//
// The app's default i18n (./i18n.jsx) is a single shared instance whose language is switched
// client-side — fine for the English root routes, but not safe to drive server-rendered
// localized routes (concurrent requests for different locales would race on one mutable
// `.language`). Instead each locale gets ONE instance, locked to its language, built
// synchronously the first time it is asked for. Because an instance's language never changes,
// concurrent SSR requests for the same locale share it safely and each locale renders its own
// real strings during SSR (crawlable localized HTML).
//
// The locale's strings come in as an argument: the `[lang]` layout (a server component) reads
// them with getTranslation (./server-t.js) and passes them down. This module used to import all
// ten translation files itself, which put every language (about 530 kB gzipped) into the
// first-load JavaScript of every `/{lang}` page (plan step W2, 2026-10-07). English is imported
// here as the fallback; ./i18n.jsx imports the same file, so it costs no extra bytes.
import { createInstance } from 'i18next'
import { initReactI18next } from 'react-i18next'
import { isSupportedLocale } from './supported-locales'

import en from './locales/en/translation.json'

const INSTANCES = {}

function build(lng, translation) {
  const instance = createInstance()
  instance.use(initReactI18next).init({
    lng,
    fallbackLng: 'en',
    resources: {
      en: { translation: en },
      [lng]: { translation: translation ?? en },
    },
    react: { useSuspense: false },
    interpolation: { escapeValue: false },
    // Synchronous init (inline resources) → the instance is READY before it renders, so t()
    // resolves during SSR without suspending. Called `initImmediate` before i18next 24; i18next
    // 26 ignores that name.
    initAsync: false,
  })
  return instance
}

// The language-locked i18n instance for `lang` (English for anything unknown), with
// `translation` as its strings: built once per locale, then shared.
export function getI18nInstance(lang, translation) {
  const lng = isSupportedLocale(lang) ? lang : 'en'
  INSTANCES[lng] ??= build(lng, lng === 'en' ? en : translation)
  return INSTANCES[lng]
}
